import React from "react";
import { Container, Row, Button } from "react-bootstrap";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import "./ProgramsBanner.css";
import { useDonation } from "../Components/DonationContext";
import { useNavigate } from "react-router-dom";
import "./DisabilityInclusion.css";

// Animation variants for Framer Motion
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

// Simple inline icon set so we don't need to add an icon library dependency.
// Each icon inherits color from the badge via currentColor.
const IconInstitutional = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M5 21V9l7-5 7 5v12" />
    <path d="M9 21v-6h6v6" />
  </svg>
);

const IconCommunity = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="8" r="3" />
    <path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" />
    <circle cx="17" cy="7" r="2.3" />
    <path d="M16 12.2c2.9.5 5 2.6 5 5.3" />
  </svg>
);

const IconEarlyID = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
    <path d="M11 8v3l2 2" />
  </svg>
);

const IconEmpower = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 8.6c0 5-8.8 10.4-8.8 10.4S3.2 13.6 3.2 8.6a4.6 4.6 0 0 1 8.8-1.8 4.6 4.6 0 0 1 8.8 1.8Z" />
  </svg>
);

const IconHeart = () => (
  <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.8 8.6c0 5-8.8 10.4-8.8 10.4S3.2 13.6 3.2 8.6a4.6 4.6 0 0 1 8.8-1.8 4.6 4.6 0 0 1 8.8 1.8Z" />
  </svg>
);

// Program data — content unchanged, only presentation is restyled
const programs = [
  {
    icon: <IconInstitutional />,
    image: "/assets/images/sections/Od1.jpg",
    title: "Institutional Rehabilitation",
    text: "Vikash has set up 4 residential and day care centres for early intervention and to initiate the rehabilitation process for Children with Intellectual & Developmental Disabilities.",
    reverse: false,
  },
  {
    icon: <IconCommunity />,
    image: "/assets/images/sections/od2.jpg",
    title: "Community Based Rehabilitation",
    text: "We believe CBR is key to creating equal opportunities. Our program promotes health, education, livelihood, social empowerment, and accessibility for PwDs to ensure they live with dignity and independence.",
    reverse: true,
  },
  {
    icon: <IconEarlyID />,
    image: "/assets/images/sections/od3.jpg",
    title: "Early Identification & Intervention",
    text: "Our professional team organizes diagnosis camps at the village and Panchayat level to identify disabilities early. We then collaborate with government authorities for medical certification and refer children to our centres for rehabilitation.",
    reverse: false,
  },
  {
    icon: <IconEmpower />,
    image: "/assets/images/sections/od4.jpg",
    title: "Empowering People's Organisations",
    text: "We emphasize the promotion and strengthening of Disabled People's Organizations (DPOs), Parent Associations, Caregiver Associations, Self Advocates, and Self-Help Groups to build a strong support network.",
    reverse: true,
  },
];

// Renamed component
const DisabilityInclusion = () => {
  const { openDonation } = useDonation();
  const navigate = useNavigate();

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="page-header">
        <div
          className="page-header__bg"
          style={{
            backgroundImage: "url(/assets/images/backgrounds/Opportunities-for-the-Disabled.jpg)",
          }}
        ></div>
        <div className="page-header__overlay"></div>
        <div className="shape1 float-bob-x">
          <img src="assets/images/shapes/page-header-shape1.png" alt="" />
        </div>
        <div className="shape2">
          <img src="assets/images/shapes/page-header-shape2.png" alt="" />
        </div>
        <Container>
          <div className="page-header__inner text-center">
            <motion.h2
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Opportunities for the Disabled
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{ color: "#dad2d0", fontSize: "22px", marginTop: "20px" }}
            >
              Vikash creates opportunities for disabled people by offering support, resources, and inclusive programs that promote empowerment and independence.
            </motion.p>
          </div>
        </Container>
      </section>

      {/* Core Programs Section */}
      <section className="programs-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="text-center programs-section__heading"
          >
            <span className="section-eyebrow">Our Core Programs for Inclusion</span>
            <h2 className="section-title">We believe in a multi-pronged approach</h2>
            <p className="section-subtitle">
              We believe in a multi-pronged approach, from early intervention to community-level empowerment.
            </p>
          </motion.div>

          {programs.map((program, index) => (
            <motion.div
              key={program.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
              className="program-card"
            >
              <Row className="align-items-center g-0">
                <motion.div
                  className={`col-lg-6 program-card__media ${program.reverse ? "order-lg-2" : "order-lg-1"}`}
                  variants={staggerItem}
                >
                  <img src={program.image} alt={program.title} className="program-card__img" />
                </motion.div>
                <motion.div
                  className={`col-lg-6 program-card__content ${program.reverse ? "order-lg-1" : "order-lg-2"}`}
                  variants={staggerItem}
                >
                  <span className="program-card__badge">{program.icon}</span>
                  <h3 className="program-card__title">{program.title}</h3>
                  <p className="program-card__text">{program.text}</p>
                </motion.div>
              </Row>
            </motion.div>
          ))}
        </Container>
      </section>

      {/* Call To Action Section (Reusable) */}
      <section className="cta-section">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeIn}
            className="cta-card"
          >
            <Row className="align-items-center g-0">
              <div className="col-lg-7 cta-card__content">
                <span className="cta-card__badge">
                  <IconHeart />
                </span>
                <h2 className="cta-card__title">Support Inclusive Opportunities</h2>
                <p className="cta-card__text">
                  Your donation helps us provide critical resources and rehabilitation services to people with disabilities.
                </p>
                <div className="cta-card__actions">
                  <Button variant="light" size="lg" className="cta-card__btn cta-card__btn--donate" onClick={openDonation}>
                    Donate Now
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    className="cta-card__btn cta-card__btn--join"
                    onClick={() => navigate("/interns")}
                  >
                    Join Us
                  </Button>
                </div>
              </div>
              <div className="col-lg-5 cta-card__media">
                <div className="cta-card__media-ring">
                  <img
                    src="/assets/images/backgrounds/do6.png"
                    alt="Support inclusive opportunities"
                    className="cta-card__img"
                  />
                </div>
              </div>
            </Row>
          </motion.div>
        </Container>
      </section>

      <Footer />

      <a href="#" data-target="html" className="scroll-to-target scroll-to-top">
        <span className="scroll-to-top__wrapper">
          <span className="scroll-to-top__inner" />
        </span>
        <span className="scroll-to-top__text"> Go Back Top</span>
      </a>
    </>
  );
};

export default DisabilityInclusion;